import { useCallback } from "react";
import { NAMESPACES } from "@/locales";
import { getLocale, readLanguage, SITE_LANGUAGES, useLanguage, type SiteLanguage } from "./language";

type Namespace = (typeof NAMESPACES)[keyof typeof NAMESPACES];
type UnionToIntersection<U> = (U extends unknown ? (value: U) => void : never) extends (value: infer I) => void ? I : never;
type MasterMessages = UnionToIntersection<Namespace["ru"]>;

export type TranslationKey = Extract<keyof MasterMessages, string>;
export type TranslationValues = Record<string, string | number>;
export type Translate = (key: TranslationKey, values?: TranslationValues) => string;

type Dictionary = Record<string, string>;

function buildDictionary(language: SiteLanguage): Dictionary {
  return Object.assign({}, ...Object.values(NAMESPACES).map((namespace) => namespace[language] as Dictionary)) as Dictionary;
}

const DICTIONARIES = Object.fromEntries(SITE_LANGUAGES.map(({ id }) => [id, buildDictionary(id)])) as Record<SiteLanguage, Dictionary>;
const MASTER = DICTIONARIES.ru;
const PLURAL_FORM = /^(zero|one|two|few|many|other):/;
const reportedMissing = new Set<string>();

function selectPlural(message: string, language: SiteLanguage, count: number) {
  const forms = new Map<string, string>();
  for (const part of message.split("|")) {
    const match = PLURAL_FORM.exec(part);
    if (match?.[1]) forms.set(match[1], part.slice(match[0].length));
  }
  return forms.get(new Intl.PluralRules(getLocale(language)).select(count)) ?? forms.get("other") ?? message;
}

function interpolate(message: string, values: TranslationValues) {
  return message.replace(/\{(\w+)\}/g, (placeholder, name: string) => (name in values ? String(values[name]) : placeholder));
}

/**
 * Translated text for `key`. Every language is typechecked to be complete, so the master (Russian) fallback
 * only covers keys built at runtime from backend codes; a key that is absent everywhere is reported once and
 * rendered as-is rather than as `undefined`.
 */
export function translate(language: SiteLanguage, key: TranslationKey, values?: TranslationValues): string {
  let message = DICTIONARIES[language]?.[key] ?? MASTER[key];
  if (message === undefined) {
    if (!reportedMissing.has(key)) {
      reportedMissing.add(key);
      console.warn(`[i18n] missing translation key: ${key}`);
    }
    return key;
  }
  if (PLURAL_FORM.test(message)) message = selectPlural(message, language, Number(values?.["count"] ?? 0));
  return values ? interpolate(message, values) : message;
}

export function hasTranslation(key: string): key is TranslationKey {
  return key in MASTER;
}

/** Translation in the currently selected language, for non-React code that runs during render or an event. */
export function t(key: TranslationKey, values?: TranslationValues) {
  return translate(readLanguage(), key, values);
}

/** Translator bound to the selected language; components using it re-render when the language changes. */
export function useT(): Translate {
  const language = useLanguage();
  return useCallback((key: TranslationKey, values?: TranslationValues) => translate(language, key, values), [language]);
}

/** @internal Exposed for the dictionary completeness tests. */
export const __dictionaries = DICTIONARIES;
